import { useState } from 'react';
import type { CreateTariefInput, Tarief, TariefPeriode } from '@fleet/shared';
import { euro, fmtDate, parseEuro, perMaand } from '@fleet/shared';
import { Button, Input } from '../../design-system';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';

const PERIODE_OPTIES = [
  { value: 'MAAND', label: 'Per maand' },
  { value: 'KWARTAAL', label: 'Per kwartaal' },
  { value: 'JAAR', label: 'Per jaar' },
];

/**
 * Popup: record a new amount for a fixed cost, from a date.
 *
 * Not an edit of the current amount — that is the whole point. The rate that was
 * valid before keeps its own period, so a cost computed over an earlier month
 * still uses it. The form says out loud what the change is worth per month,
 * because that is the number the charts move by and a quarterly assessment
 * against a yearly premium is otherwise hard to compare.
 */
export function TariefToevoegen({
  soort,
  label,
  standaardPeriode,
  huidig,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  soort: string;
  /** Human name of this kind of fixed cost, e.g. "Wegenbelasting". */
  label: string;
  standaardPeriode: TariefPeriode;
  /** The rate in force now, so the delta can be shown while typing. */
  huidig: Tarief | null;
  busy?: boolean;
  error?: string;
  onSubmit: (input: CreateTariefInput) => void;
  onCancel: () => void;
}) {
  const [bedrag, setBedrag] = useState('');
  const [periode, setPeriode] = useState<TariefPeriode>(huidig?.periode ?? standaardPeriode);
  const [geldigVan, setGeldigVan] = useState(() => new Date().toISOString().slice(0, 10));
  const [notitie, setNotitie] = useState('');
  const [fout, setFout] = useState('');

  const waarde = parseEuro(bedrag);
  const nieuwPerMaand = waarde == null ? null : perMaand({ bedrag: waarde, periode });
  const oudPerMaand = huidig ? perMaand(huidig) : null;
  const delta = nieuwPerMaand != null && oudPerMaand != null ? nieuwPerMaand - oudPerMaand : null;

  // De ingangsdatum moet ná de lopende periode beginnen, anders draait de keten
  // om; de server weigert dat ook, maar dan pas na een mislukte opslag.
  const teVroeg = huidig != null && geldigVan <= huidig.geldigVan;

  function opslaan(e: React.FormEvent) {
    e.preventDefault();
    if (waarde == null) {
      setFout(bedrag.trim() ? `"${bedrag}" is geen geldig bedrag.` : 'Vul een bedrag in.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(geldigVan)) {
      setFout('Vul een ingangsdatum in — vanaf wanneer dit bedrag geldt.');
      return;
    }
    if (teVroeg) {
      setFout(
        `Het huidige tarief loopt al vanaf ${fmtDate(huidig!.geldigVan)}. Kies een latere datum.`,
      );
      return;
    }
    setFout('');
    onSubmit({ soort, bedrag: waarde, periode, geldigVan, notitie: notitie.trim() || null });
  }

  return (
    <Modal open onClose={onCancel} title={`${label} wijzigen`} width={470}>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666666', lineHeight: 1.5 }}>
        {huidig ? (
          <>
            Nu <strong style={{ color: '#111111' }}>{euro(huidig.bedrag)}</strong>{' '}
            {PERIODE_OPTIES.find((p) => p.value === huidig.periode)?.label.toLowerCase()}, sinds{' '}
            {fmtDate(huidig.geldigVan)}. Het nieuwe bedrag komt daar achteraan; de oude periode
            blijft staan, zodat kosten over eerdere maanden op het oude tarief blijven rekenen.
          </>
        ) : (
          <>
            Het eerste bedrag dat we van deze auto vastleggen. Vanaf de ingangsdatum telt het mee in
            de kosten.
          </>
        )}
      </p>

      <form onSubmit={opslaan} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input
          label="Nieuw bedrag excl. btw"
          value={bedrag}
          placeholder="bv. 219,00"
          onChange={(e) => setBedrag(e.target.value)}
          autoFocus
        />
        <Dropdown
          label="Periode"
          block
          value={periode}
          onChange={(v) => setPeriode(v as TariefPeriode)}
          options={PERIODE_OPTIES}
        />
        <Input
          label="Geldig vanaf"
          type="date"
          value={geldigVan}
          onChange={(e) => setGeldigVan(e.target.value)}
        />
        <Input
          label="Notitie"
          value={notitie}
          placeholder="optioneel — bv. aanslag 2027 of nieuwe polis"
          onChange={(e) => setNotitie(e.target.value)}
        />

        {nieuwPerMaand != null && (
          <div style={{ fontSize: 12.5, color: '#666666', lineHeight: 1.6 }}>
            Komt neer op <strong style={{ color: '#111111' }}>{euro(nieuwPerMaand)}</strong> per
            maand
            {delta != null && Math.abs(delta) >= 0.005 && (
              <>
                {' '}
                —{' '}
                <strong style={{ color: delta > 0 ? '#B0543B' : '#3E7D63' }}>
                  {delta > 0 ? '+' : '−'} {euro(Math.abs(delta))}
                </strong>{' '}
                per maand ten opzichte van nu
              </>
            )}
            .
          </div>
        )}

        {(fout || error) && (
          <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{fout || error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" type="button" onClick={onCancel}>
            Annuleren
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? 'Opslaan…' : 'Tarief opslaan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
